import AuthLayout from "./AuthLayout";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import OTPInput from "react-otp-input";
import { useState, useEffect } from "react";
import { useSendOtpMutation, useVerifyOtpMutation } from "../../service/auth/api";
import { toast } from "react-toastify";
import React from "react";

const OtpVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, otpType } = location.state || {}; // Retrieve email and otpType from navigation state

  const [otp, setOtp] = useState<string>("");
  const [timer, setTimer] = useState<number>(30);
  const [canResend, setCanResend] = useState<boolean>(false);

  const [verifyOtp, { isLoading: isVerifying }] = useVerifyOtpMutation();
  const [sendOtp, { isLoading: isResending }] = useSendOtpMutation();

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleResendOtp = async () => {
    if (!canResend) return;

    try {
      // Use otpType 4 (VERIFICATION) for resend as logic requested
      // or simply reuse the original otpType if that's what was intended minus the "4" instruction.
      // Instruction says: "hit same send otp api with otp type 4"
      const resendOtpType = 4; // Or OTP_FOR.VERIFICATION

      const result = await sendOtp({
        email: email,
        otpType: resendOtpType
      }).unwrap();

      toast.success(result.message || "OTP resent successfully");
      setTimer(30);
      setCanResend(false);
      setOtp(""); // Clear OTP field
    } catch (err: any) {
      console.error("Resend OTP Error:", err);
      toast.error(err?.data?.message || "Failed to resend OTP");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 4) {
      toast.error("Please enter a valid 4-digit OTP");
      return;
    }

    try {
      const payload = {
        role: 3, // Assuming Admin/Vendor role based on context or "3" from example
        otp: otp,
        otpType: otpType || 1, // Use passed type or default to 1 (from example)
        email: email, // Use email
        // phone: "...", // Not available
        // countryCode: "...", // Not available
        deviceType: "WEB",
        deviceToken: "dummy_token" // Placeholder
        // password: "..." // Not available for forgot password flow typically
      };

      const result = await verifyOtp(payload).unwrap();

      const token = result?.data?.token;
      toast.success(result.message || "OTP verified successfully");
      navigate("/reset-password", { state: { email, otp: otp, token } }); // Pass token to reset password
    } catch (err: any) {
      console.error("Verify OTP Error:", err);
      toast.error(err?.data?.message || "Invalid OTP");
      setOtp(""); // Clear input fields on incorrect OTP
    }
  };

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Verify Your Email</h2>
        <p>We have Sent OTP to {email || "your email"} to verify your email address and activate your account entering the OTP</p>

        <form className="form" onSubmit={handleSubmit}>
          <div className="control_group">
            <div className="opt_fields">
              <OTPInput
                value={otp}
                onChange={setOtp}
                numInputs={4}
                renderInput={(props) => <input {...props} id="otp" />}
                inputType="tel"
                shouldAutoFocus
              />
            </div>
            <div className="auth_bottom">
              <p>
                <button
                  type="button"
                  className="link"
                  onClick={handleResendOtp}
                  disabled={!canResend || isResending}
                  style={{ opacity: canResend ? 1 : 0.5, cursor: canResend ? 'pointer' : 'not-allowed' }}
                >
                  {isResending ? "Resending..." : canResend ? "Resend OTP" : `Resend OTP (${timer}s)`}
                </button>
              </p>
            </div>
          </div>
          <div className="form_btn">
            <Button
              type="submit"
              className="btn btn_primary w_100"
              disabled={isVerifying}
            >
              {isVerifying ? "Verifying..." : "Reset Password"}
            </Button>
          </div>
        </form>
        <div className="auth_bottom">
          <p>
            <button className="link" onClick={() => navigate("/")}>
              <KeyboardBackspaceIcon /> Return to Login
            </button>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default OtpVerification;
